import type { AppDispatch } from '../index';
import { getContent } from '../../lib/api/content-api-client';
import { getPublicWebsiteConfig } from '../../lib/api/config';
import {
  contentFetchRequested,
  contentFetchSucceeded,
  contentFetchFailed,
} from '../slices/content-slice';

/**
 * Content thunks (task 5.2, public-website spec).
 *
 * Async actions wrapping `getContent` from the content API client per
 * design §3.1 (Thunks layer): the only place network side effects
 * live in the Redux layer. The thunk dispatches the three slice
 * actions in lockstep — `requested` on entry, `succeeded` with the
 * parsed payload on success, `failed` with a string error on
 * rejection — so reducers stay pure and `state.content.statusByDate`
 * tracks the in-flight lifecycle the components in 5.4 / 5.5 read.
 *
 * The thunk reads its base URL + API key through
 * `getPublicWebsiteConfig()` rather than receiving them as
 * arguments, matching the design §3.1 contract that thunks are the
 * single layer that bridges Redux to the runtime config injected at
 * SPA bootstrap.
 *
 * Function-length budget: every export below stays under 20 lines
 * per Requirement 3.7.
 */

/** Input shape for `fetchContent`. */
export interface FetchContentInput {
  /** YYYY-MM-DD date to fetch the daily payload for. */
  readonly date: string;
}

/**
 * Fetch the day's content payload and mirror the call into the
 * content slice.
 *
 * @param input The date to fetch.
 * @returns A thunk that resolves once the matching slice action has
 *   been dispatched (`succeeded` on 2xx, `failed` on error).
 */
export function fetchContent(input: FetchContentInput) {
  return async (dispatch: AppDispatch): Promise<void> => {
    dispatch(contentFetchRequested({ date: input.date }));
    try {
      const config = getPublicWebsiteConfig();
      const payload = await getContent(
        { date: input.date },
        { baseUrl: config.publicApiInvokeUrl, apiKey: config.publicApiKey },
      );
      dispatch(contentFetchSucceeded({ date: input.date, payload }));
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      dispatch(contentFetchFailed({ date: input.date, error: message }));
    }
  };
}
